import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home as HomeIcon, Search, PlusCircle, MessageSquare, User, Image, Video } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { isServiceRole } from "../lib/roles";

/** Fixed bottom navigation — visible only on mobile (< md). Mirrors the 99acres bottom bar. */
export default function BottomNav() {
    const { pathname } = useLocation();
    const { user } = useAuth();
    const [chatUnread, setChatUnread] = useState(0);

    // Poll chat unread count every 30s while logged in — badge on the chat tab.
    useEffect(() => {
        if (!user) { setChatUnread(0); return; }
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/chat/unread-count");
                if (!cancelled) setChatUnread(data?.count || 0);
            } catch { /* silent — non-paid users get 402 which is fine */ }
        };
        load();
        const id = setInterval(load, 30000);
        const refresh = () => load();
        window.addEventListener("gpm:chat:refresh", refresh);
        return () => { cancelled = true; clearInterval(id); window.removeEventListener("gpm:chat:refresh", refresh); };
    }, [user]);

    // Hide the bottom nav on auth screens where the user is filling forms
    if (pathname === "/login" || pathname === "/register" || pathname.startsWith("/verify-otp") || pathname.startsWith("/reset-password") || pathname.startsWith("/forgot-password")) {
        return null;
    }

    // Service professionals never post properties — swap the primary CTA to "Portfolio" so
    // they land in their own portfolio manager instead.
    const isSvc = user && isServiceRole(user.role);
    const primary = isSvc
        ? { key: "portfolio", label: "Portfolio", to: "/my-portfolio", icon: Image, primary: true }
        : { key: "sell", label: "Sell/Rent", to: user ? "/add-property" : "/register?intent=sell", icon: PlusCircle, primary: true };

    const items = [
        { key: "home",   label: "Home",     to: "/",              icon: HomeIcon },
        { key: "search", label: "Search",   to: "/properties",    icon: Search },
        { key: "reels",  label: "Reels",    to: "/reels",         icon: Video },
        primary,
        { key: "chat",   label: "Chat",     to: user ? "/chat" : "/login?next=/chat",   icon: MessageSquare, badge: chatUnread },
        { key: "profile", label: "Profile", to: user ? "/profile" : "/login?next=/profile", icon: User },
    ];

    const isActive = (to) => {
        if (to === "/") return pathname === "/";
        return pathname.startsWith(to);
    };

    return (
        <>
            {/* Spacer so page content isn't hidden behind the fixed bar on mobile */}
            <div className="md:hidden h-16" aria-hidden="true" />

            <nav
                className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-navy-500/10 shadow-[0_-6px_20px_-8px_rgba(0,38,87,0.15)]"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
                data-testid="bottom-nav"
            >
                <div className="grid grid-cols-6">
                    {items.map((it) => {
                        const Icon = it.icon;
                        const active = isActive(it.to);
                        if (it.primary) {
                            return (
                                <Link
                                    key={it.key}
                                    to={it.to}
                                    data-testid={`bottom-nav-${it.key}`}
                                    className={`relative flex flex-col items-center justify-center py-2.5 gap-1 transition-colors ${
                                        active ? "text-gold-600" : "text-navy-500/60 hover:text-navy-500"
                                    }`}
                                >
                                    <div className="relative">
                                        <Icon className={`w-5 h-5 ${active ? "fill-gold-500/10" : ""}`} />
                                    </div>
                                    <span className={`text-[10px] leading-none ${active ? "font-bold" : "font-semibold"}`}>{it.label}</span>
                                </Link>
                            );
                        }
                        return (
                            <Link
                                key={it.key}
                                to={it.to}
                                data-testid={`bottom-nav-${it.key}`}
                                className={`relative flex flex-col items-center justify-center py-2.5 gap-1 transition-colors ${
                                    active ? "text-gold-600" : "text-navy-500/60 hover:text-navy-500"
                                }`}
                            >
                                <div className="relative">
                                    <Icon className={`w-5 h-5 ${active ? "fill-gold-500/10" : ""}`} />
                                    {it.badge > 0 && (
                                        <span
                                            data-testid={`bottom-nav-${it.key}-badge`}
                                            className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-extrabold flex items-center justify-center border-2 border-white"
                                        >
                                            {it.badge > 99 ? "99+" : it.badge}
                                        </span>
                                    )}
                                </div>
                                <span className={`text-[10px] leading-none ${active ? "font-bold" : "font-semibold"}`}>{it.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </nav>
        </>
    );
}
